import { put, call, takeLatest, takeEvery } from 'redux-saga/effects';

import AuthUtil from '../../AuthUtil';
import { LOAN } from '../../../components/global/Server/endpoints';
import { hideError, showErrorMessage } from '../../actions/global';
import {
    LIST_CUSTOMER_LOAN,
    SAVE_CUSTOMER_LOAN,
    CREATE_BORROWER,
    EDIT_BORROWER,
    ADD_TRANSACTION,
    EDIT_TRANSACTION,
    DELETE_TRANSACTION,
    DELETE_BORROWER,
} from '../../constants/loan';
import {
    storeCustomerLoan,
    storeBorrowers,
    storeBorrower,
    removeBorrower,
} from '../../actions/loan';
import { SERVER_FAILED_ERROR_MESSAGE } from '../../../components/dashboard/constants';

// ---------------------------------------------------------------------------
//  LIST
// ---------------------------------------------------------------------------

function* listCustomerLoanSaga(action) {
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(LOAN.LIST_CUSTOMER_LOAN, action.data),
        );

        if (response.status === 200 && response.data.success) {
            // New shape: { borrowers, totalOutstanding, activeLoans, ... }
            if (response.data.borrowers) {
                yield put(
                    storeBorrowers({
                        borrowers: response.data.borrowers,
                        totalOutstanding: response.data.totalOutstanding,
                        activeLoans: response.data.activeLoans,
                        interestEarningCount:
                            response.data.interestEarningCount,
                        monthlyInterestIncome:
                            response.data.monthlyInterestIncome,
                    }),
                );
            }
            // Legacy shape: keep for backward compat with any old callers
            if (response.data.loanHistory) {
                yield put(storeCustomerLoan(response.data.loanHistory));
            }
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
    }
}

// ---------------------------------------------------------------------------
//  Generic helper for write operations that return { success, borrower }
// ---------------------------------------------------------------------------

function* writeAndStore(endpoint, action) {
    yield put(hideError());
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(endpoint, action.data),
        );

        if (response.status === 200 && response.data.success) {
            if (response.data.borrower) {
                yield put(storeBorrower(response.data.borrower));
            }
            if (typeof action.callback === 'function') {
                action.callback(null, response.data.message);
            }
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') {
                action.callback(message, null);
            }
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') {
            action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
        }
    }
}

function* createBorrowerSaga(action) {
    yield* writeAndStore(LOAN.CREATE_BORROWER, action);
}

function* editBorrowerSaga(action) {
    yield* writeAndStore(LOAN.EDIT_BORROWER, action);
}

function* addTransactionSaga(action) {
    yield* writeAndStore(LOAN.ADD_TRANSACTION, action);
}

function* editTransactionSaga(action) {
    yield* writeAndStore(LOAN.EDIT_TRANSACTION, action);
}

function* deleteTransactionSaga(action) {
    yield* writeAndStore(LOAN.DELETE_TRANSACTION, action);
}

// ---------------------------------------------------------------------------
//  DELETE BORROWER  (returns { success, borrowerId })
// ---------------------------------------------------------------------------

function* deleteBorrowerSaga(action) {
    yield put(hideError());
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(LOAN.DELETE_BORROWER, action.data),
        );

        if (response.status === 200 && response.data.success) {
            yield put(
                removeBorrower(
                    response.data.borrowerId || action.data.borrowerId,
                ),
            );
            if (typeof action.callback === 'function') {
                action.callback(null, response.data.message);
            }
        } else {
            const message = response.data?.message || 'Request failed';
            if (typeof action.callback === 'function') {
                action.callback(message, null);
            }
            yield put(showErrorMessage(message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
        if (typeof action.callback === 'function') {
            action.callback(SERVER_FAILED_ERROR_MESSAGE, null);
        }
    }
}

// ---------------------------------------------------------------------------
//  LEGACY save action — preserved
// ---------------------------------------------------------------------------

function* saveCustomerLoanSaga(action) {
    yield put(hideError());
    try {
        const response = yield call(() =>
            AuthUtil.postRequest(LOAN.SAVE_CUSTOMER_LOAN, action.data),
        );

        if (response.status === 200 && response.data.success) {
            if (typeof action.callback === 'function') {
                action.callback(null, response.data.message);
            }
        } else {
            if (typeof action.callback === 'function') {
                action.callback('error', response.data.message);
            }
            yield put(showErrorMessage(response.data.message));
        }
    } catch (error) {
        yield put(showErrorMessage(SERVER_FAILED_ERROR_MESSAGE));
    }
}

export default function* loanSagas() {
    yield takeLatest(LIST_CUSTOMER_LOAN, listCustomerLoanSaga);
    yield takeLatest(SAVE_CUSTOMER_LOAN, saveCustomerLoanSaga);
    yield takeEvery(CREATE_BORROWER, createBorrowerSaga);
    yield takeEvery(EDIT_BORROWER, editBorrowerSaga);
    yield takeEvery(ADD_TRANSACTION, addTransactionSaga);
    yield takeEvery(EDIT_TRANSACTION, editTransactionSaga);
    yield takeEvery(DELETE_TRANSACTION, deleteTransactionSaga);
    yield takeEvery(DELETE_BORROWER, deleteBorrowerSaga);
}
